import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { ButtonBase } from '@mui/material';

import { theme } from '../../../themes/default-theme';
import lightenDarkenColor from '../../../util/lightenDarkenColor';

// --- Styled base component ---
const CtaBase = styled(ButtonBase, {
  shouldForwardProp: (prop) =>
    ![
      'primary',
      'secondary',
      'yellow',
      'outline',
      'outlineWhite',
      'uppercase',
      'inline',
      'small',
      'bg',
      'color',
    ].includes(prop),
})`
  /* High specificity to override MUI emotion styles */

  background: #0170d4;
  border-radius: 24px;
  border: 0;
  color: white;
  font-size: 13px;
  width: 100%;
  padding: 9px 30px;
  text-transform: none;
  text-decoration: none;
  font-weight: 700;
  box-sizing: border-box;
  text-transform: uppercase;

  ${props => props.inline && `
    width: auto;
  `}

  ${theme.breakpoints.up('md')} {
    padding: 13px 30px;
    font-size: 15px;
  }

  ${props => props.disabled && `opacity: 0.5;`}

  &:hover {
    background: #0c5393;
  }

  ${props =>
    props.primary &&
    css`
      &&& {
        background: ${theme.palette.vividBlue};
        &:hover {
          background: #0c5393;
        }
      }
    `}

  ${props =>
    props.secondary &&
    css`
      &&& {
        background: ${theme.palette.skyBlue};
        &:hover {
          background: #0d7abe;
        }
      }
    `}

  ${props =>
    props.yellow &&
    css`
      &&& {
        background: ${theme.palette.yellow};
        color: ${theme.palette.charcoal};
        &:hover {
          background: #e8bf16;
        }
      }
    `}

  ${props =>
    props.uppercase &&
    css`
      &&& {
        text-transform: uppercase;
      }
    `}

  ${props =>
    props.outline &&
    css`
      &&& {
        background: none;
        ${props.outlineWhite
          ? `
            color: white;
            border: 2px solid white;
          `
          : `
            color: ${
              props.yellow
                ? theme.palette.yellow
                : props.secondary
                ? theme.palette.skyBlue
                : theme.palette.vividBlue
            };
            border: 2px solid ${
              props.yellow
                ? theme.palette.yellow
                : props.secondary
                ? theme.palette.skyBlue
                : theme.palette.vividBlue
            };
          `}
        padding: 8px 20px;
        &:hover {
          background: none;
          filter: brightness(80%);
        }
        ${theme.breakpoints.up('md')} {
          padding: 13px 30px;
        }
      }
    `}

  ${props =>
    props.inline &&
    css`
      &&& {
        display: inline-block;
        width: auto;
      }
    `}

  ${props =>
    props.bg &&
    css`
      &&& {
        background: ${props.bg};
        &:hover {
          background: ${lightenDarkenColor(props.bg, -20)};
        }
      }
    `}

  ${props =>
    props.color &&
    css`
      &&& {
        border-color: ${props.color};
        color: ${props.color};
      }
    `}
`;

// --- Wrapper that forces class="cta" always ---
const Cta = ({ className = '', ...rest }) => {
  return <CtaBase className={`cta ${className}`} {...rest} />;
};

export default Cta;
